import React, { useCallback, useRef, useMemo, useState } from "react";
import { ReactCropperElement } from "react-cropper";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";

import type { IOption } from "models/Form";
import type { IFRAttachment } from "models/HaulPay";
import { FileProgress, FileUploadProvider, useUpload } from "context/FileUpload";
import { showCustomActionSheet } from "components/CustomActionSheet";
import { selectUserDetails } from "store/user/selector";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { UPLOAD_CATEGORY } from "constants/base";
import ChunkedUploader from "common/ChunkUploader";

import { FileUploader } from "../../../Shared/EditFundingRequestV2/Manage/components/FileUploader";
import { CropModal } from "../../../Shared/EditFundingRequestV2/Manage/components/CropModal";
import { CROPPED_CANVAS_OPTIONS } from "../../../Shared/EditFundingRequestV2/Manage/components/imageEditor/cropperUtils";
import { DocumentPreviewModal } from "../../../Shared/EditFundingRequestV2/Manage/components/DocumentPreviewModal";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILE_SIZE_UPLOAD = 25 * 1024 * 1024; // 25MB
const FILE_SIZE_LIMIT_ERROR =
  "This file exceeds the 25MB limit. Please reduce the file size and try again.";

interface UploadSectionProps {
  fundingRequestId: string;
  attachments: IFRAttachment[];
  onAttachmentsChange: (attachments: IFRAttachment[]) => void;
  editingAttachment?: IFRAttachment | null;
  setEditingAttachment?: (attachment: IFRAttachment | null) => void;
}

const UploadSectionInner: React.FC<UploadSectionProps> = ({
  fundingRequestId,
  attachments,
  onAttachmentsChange,
  editingAttachment,
  setEditingAttachment
}) => {
  const userDetails = useSelector(selectUserDetails);

  const {
    files,
    addFile,
    setFileProgress,
    setFileStatus,
    removeFile,
    updateFile,
    replaceFile,
    updateFileCategory,
    updateFileVisibility
  } = useUpload();

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState<number | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  const cropperRef = useRef<ReactCropperElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsRef = useRef<IFRAttachment[]>(attachments);
  const processedAttachmentRef = useRef<string | null>(null);
  const pendingEditFileIdRef = useRef<string | null>(null);

  React.useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Handle editing existing attachments
  React.useEffect(() => {
    if (editingAttachment && processedAttachmentRef.current !== editingAttachment.id) {
      processedAttachmentRef.current = editingAttachment.id;

      const previewUrl =
        editingAttachment.previewUrl ||
        editingAttachment.preview_url ||
        editingAttachment.downloadUrl ||
        editingAttachment.download_url;

      if (!previewUrl) {
        showErrorMessage("Cannot edit attachment: No preview available");
        setEditingAttachment?.(null);
        return;
      }

      const filename = editingAttachment.filename || "";
      const extension = filename.split(".").pop()?.toLowerCase();
      const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");

      if (!isImage) {
        showErrorMessage("Can only edit image attachments in crop mode");
        setEditingAttachment?.(null);
        return;
      }

      const controller = new AbortController();
      let addedFileId: string | null = null;
      let removeOnAbort = true;

      fetch(previewUrl, { signal: controller.signal })
        .then((res) => res.blob())
        .then((blob) => {
          if (controller.signal.aborted) return;

          const file = new File([blob], editingAttachment.filename || "image.jpg", {
            type: blob.type || "image/jpeg"
          });
          const fileProgress = addFile(file);
          addedFileId = fileProgress.id;

          const categoryStr =
            typeof editingAttachment.category === "string"
              ? editingAttachment.category
              : Array.isArray(editingAttachment.category)
                ? editingAttachment.category.join(",")
                : "";

          const visibleTo = editingAttachment.visibleTo || editingAttachment.visible_to || [];

          updateFileCategory(fileProgress.id, categoryStr);
          updateFileVisibility(fileProgress.id, Array.isArray(visibleTo) ? visibleTo : []);
          updateFile(fileProgress.id, { preview: previewUrl });

          pendingEditFileIdRef.current = fileProgress.id;
          setIsEditMode(true);
          // Successfully initialized edit flow; do not remove file on normal unmount
          removeOnAbort = false;
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          showErrorMessage("Failed to load attachment for editing");
          setEditingAttachment?.(null);
          processedAttachmentRef.current = null;
        });

      return () => {
        controller.abort();
        // Only remove the added temp file if the operation was actually aborted
        if (addedFileId && removeOnAbort) {
          removeFile(addedFileId);
          pendingEditFileIdRef.current = null;
          processedAttachmentRef.current = null;
          setIsEditMode(false);
        }
      };
    } else if (!editingAttachment) {
      processedAttachmentRef.current = null;
    }
  }, [
    editingAttachment,
    setEditingAttachment,
    addFile,
    updateFileCategory,
    updateFileVisibility,
    updateFile,
    removeFile
  ]);

  // Watch for the pending file to appear in the files array and open the crop modal
  React.useEffect(() => {
    if (pendingEditFileIdRef.current && files.length > 0) {
      const fileIndex = files.findIndex((f) => f.id === pendingEditFileIdRef.current);
      if (fileIndex !== -1) {
        setCurrentFileIndex(fileIndex);
        setShowCropModal(true);
        pendingEditFileIdRef.current = null;
      }
    }
  }, [files]);

  const pushAttachment = useCallback(
    (incoming: IFRAttachment) => {
      const map = new Map<string, IFRAttachment>();
      attachmentsRef.current.forEach((att) => {
        map.set(String(att.id), att);
      });
      map.set(String(incoming.id), incoming);
      const merged = Array.from(map.values());
      attachmentsRef.current = merged;
      onAttachmentsChange(merged);
    },
    [onAttachmentsChange]
  );

  const toAttachment = useCallback((file: FileProgress, previewUrl?: string): IFRAttachment => {
    const category = file.categories || "";
    const visibleList = Array.isArray(file.visible_to) ? file.visible_to : [];

    return {
      id: String(file.id),
      filename: file.name ?? "Unnamed file",
      category,
      uploaded: dayjs().format("MM/DD/YYYY HH:mm:ss A"),
      visibleTo: visibleList,
      visible_to: visibleList,
      downloadUrl: previewUrl ?? "",
      download_url: previewUrl ?? "",
      previewUrl: previewUrl,
      preview_url: previewUrl,
      type: file.fileType ?? file.type,
      size: file.fileSize ?? file.size
    };
  }, []);

  const categories = useMemo(() => UPLOAD_CATEGORY, []);
  const isUploading = useMemo(() => files.some((f) => f.status === "uploading"), [files]);
  const currentFile = useMemo<FileProgress | null>(
    () => (currentFileIndex !== null ? files[currentFileIndex] : null),
    [currentFileIndex, files]
  );

  const currentCategoryOptions = useMemo<IOption[]>(() => {
    if (!currentFile) return [];
    const raw = ((currentFile as any).categories as string | undefined) ?? "";
    const values = raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!values.length) return [];

    return values
      .map(
        (candidate) =>
          categories.find(
            (opt) =>
              String((opt as any).value ?? (opt as any).label ?? "")
                .toLowerCase()
                .trim() === candidate.toLowerCase()
          ) as IOption | undefined
      )
      .filter((opt): opt is IOption => Boolean(opt));
  }, [currentFile, categories]);

  const addFileToUpload = useCallback((file: File): FileProgress => addFile(file), [addFile]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const isDragEvent = e.type === "dragenter" || e.type === "dragover";
    setIsDragActive(isDragEvent);
  }, []);

  const revokePreviewUrl = useCallback((preview: string | undefined) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  }, []);

  const processFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const filesArray = Array.from(newFiles);
      const valid = filesArray.filter((f) => f.size <= MAX_FILE_SIZE);

      if (valid.length < filesArray.length) {
        showErrorMessage(FILE_SIZE_LIMIT_ERROR);
      }

      let nextIndexBase = files.length;

      valid.forEach((file) => {
        const isImage = file.type.startsWith("image/");
        const fileProgress = addFileToUpload(file);

        try {
          const previewUrl = URL.createObjectURL(file);
          updateFile(fileProgress.id, { preview: previewUrl });
        } catch (error) {
          // Ignore preview URL errors
        }

        setCurrentFileIndex(nextIndexBase);

        if (isImage) {
          setShowCropModal(true);
        } else {
          setShowDocumentModal(true);
        }
        nextIndexBase += 1;
      });
    },
    [addFileToUpload, files.length, updateFile]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
        e.target.value = "";
      }
    },
    [processFiles]
  );

  const isMobileBrowser = useCallback(() => {
    if (typeof window === "undefined") return false;
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    );
  }, []);

  const handleCamera = useCallback(
    async (source: CameraSource) => {
      try {
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source,
          saveToGallery: false
        });

        if (!photo.webPath) {
          throw new Error("No photo was returned");
        }

        const path = photo.webPath.startsWith("file:")
          ? Capacitor.convertFileSrc(photo.webPath)
          : photo.webPath;
        const response = await fetch(path);
        const blob = await response.blob();
        const format = photo.format ?? "jpeg";
        const file = new File([blob], `photo.${format}`, { type: `image/${format}` });

        const simulatedEvent = {
          target: {
            files: [file],
            value: ""
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleFileInputChange(simulatedEvent);
      } catch (error) {
        showErrorMessage(
          `Camera error: ${error instanceof Error ? error.message : "Failed to get photo"}`
        );
      }
    },
    [handleFileInputChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) processFiles(dropped);
    },
    [processFiles]
  );

  const initializeUploader = useCallback(
    (file: FileProgress) => {
      if (file.size > MAX_FILE_SIZE_UPLOAD) {
        return showErrorMessage(FILE_SIZE_LIMIT_ERROR);
      }
      const fileObj = file.file;
      return new ChunkedUploader({
        url: "/upload/chunk/",
        urlCallback: `${
          UPLOAD_DOCUMENT_URLS({
            factoring_id: userDetails.factoring_id,
            funding_request_id: fundingRequestId
          }).FR_ATTACHMENT
        }/${file.id}`,
        file: fileObj,
        onProgress: (progress: number) => setFileProgress(file.id, progress)
      });
    },
    [setFileProgress, userDetails.factoring_id, fundingRequestId]
  );

  const uploadFile = useCallback(
    async (file: FileProgress) => {
      try {
        setFileStatus(file.id, "uploading");

        const uploader = initializeUploader(file);
        if (!uploader) throw new Error("Failed to initialize file uploader");

        const result = await uploader.upload();

        if (result.success) {
          setFileStatus(file.id, "completed");
          updateFile(file.id, { preview: result.previewUrl });

          const nextAttachment = toAttachment(file, result.previewUrl);
          pushAttachment(nextAttachment);

          showSuccessMessage(`File "${file.name}" uploaded successfully`);

          setTimeout(() => {
            removeFile(file.id);
          }, 1500);

          return { success: true };
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to upload file";
        setFileStatus(file.id, "error");
        showErrorMessage(`Failed to upload "${file.name}": ${message}`);
        return { success: false, error: message };
      }
    },
    [initializeUploader, setFileStatus, updateFile, removeFile, pushAttachment, toAttachment]
  );

  const handleClick = useCallback(async () => {
    if (isUploading) {
      return;
    }

    if (Capacitor.getPlatform() === "web" && !isMobileBrowser()) {
      fileInputRef.current?.click();
      return;
    }

    const { index } = await showCustomActionSheet({
      title: "Upload Options",
      message: "Select a source",
      options: [
        { title: "Take Photo", icon: "camera" },
        { title: "Choose from Gallery", icon: "images" },
        { title: "Browse Files", icon: "folder" },
        { title: "Cancel", style: "cancel" }
      ],
      displayMode: "auto"
    });

    if (index === 0) {
      await handleCamera(CameraSource.Camera);
    } else if (index === 1) {
      await handleCamera(CameraSource.Photos);
    } else if (index === 2) {
      fileInputRef.current?.click();
    }
  }, [handleCamera, isMobileBrowser, isUploading]);

  const handleCrop = useCallback(async () => {
    if (!currentFile || !cropperRef.current?.cropper) {
      showErrorMessage("No image to crop");
      return false;
    }
    const cf = currentFile;
    const canvas = cropperRef.current.cropper.getCroppedCanvas(CROPPED_CANVAS_OPTIONS);
    if (!canvas) {
      showErrorMessage("Failed to crop image");
      return false;
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) {
      showErrorMessage("Failed to crop image");
      return false;
    }

    const newFile = new File([blob], cf.name, {
      type: "image/jpeg",
      lastModified: Date.now()
    });

    replaceFile(cf.id, newFile);

    try {
      revokePreviewUrl(cf.preview);
      const previewUrl = URL.createObjectURL(blob);
      updateFile(cf.id, { preview: previewUrl });
    } catch (error) {
      // Ignore preview URL errors
    }
    return true;
  }, [currentFile, replaceFile, updateFile, revokePreviewUrl]);

  const shouldCompressImage = useCallback(async (file: File): Promise<boolean> => {
    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB < 0.2) return false;
    if (fileSizeInMB > 1) return true;

    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const needsCompression = img.width > 2000 || img.height > 2000;
        URL.revokeObjectURL(objectUrl);
        resolve(needsCompression);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(fileSizeInMB > 0.5);
      };
      img.src = objectUrl;
    });
  }, []);

  const compressImageFile = useCallback(
    async (file: File): Promise<File> => {
      try {
        const needsCompression = await shouldCompressImage(file);

        if (!needsCompression) return file;

        setIsCompressing(true);
        setCompressionProgress(0);

        const imageCompression = (await import("browser-image-compression")).default;

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 3000,
          useWebWorker: true,
          initialQuality: 0.92,
          alwaysKeepResolution: false,
          fileType: "image/jpeg",
          onProgress: (progress: number) => {
            setCompressionProgress(Math.round(progress));
          }
        };

        const compressedFile = await imageCompression(file, options);

        setIsCompressing(false);
        setCompressionProgress(100);

        return compressedFile;
      } catch (error) {
        setIsCompressing(false);
        setCompressionProgress(0);
        return file;
      }
    },
    [shouldCompressImage]
  );

  const handleUploadCurrentFile = useCallback(async () => {
    if (!currentFile) return;

    const categories = (currentFile as any).categories || "";
    if (!categories || categories.trim() === "") {
      showErrorMessage("Please select a category before uploading");
      return;
    }

    // HP-9218: an unreachable re-crop branch used to sit in front of this. handleUploadCurrentFile
    // is only reachable from CropModal's details stage, by which point ImageFramingStage -- the only
    // Cropper -- has unmounted and cropperRef.current is null, so the branch never ran and images
    // always fell through to compression. Deleting it changes nothing at runtime.
    if (currentFile.type?.startsWith("image/")) {
      try {
        const compressed = await compressImageFile(currentFile.file);
        replaceFile(currentFile.id, compressed);
      } catch (error) {
        showErrorMessage("Failed to compress image");
      }
    }

    const result = await uploadFile(currentFile);

    if (result.success) {
      // If in edit mode, delete the old attachment from the backend
      if (isEditMode && editingAttachment && fundingRequestId) {
        try {
          const { deleteFRAttachmentAPI } = await import("services/api/haulpay");

          // Wait for attachmentsRef to be updated with a bounded polling loop
          const maxWaitMs = 2000;
          const pollIntervalMs = 50;
          const startTime = Date.now();
          const newAttachmentId = currentFile.id;

          while (Date.now() - startTime < maxWaitMs) {
            const hasNewAttachment = attachmentsRef.current.some(
              (att) => String(att.id) === String(newAttachmentId)
            );
            if (hasNewAttachment) break;
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
          }

          await deleteFRAttachmentAPI(fundingRequestId, editingAttachment.id);

          const updatedAttachments = attachmentsRef.current.filter(
            (att) => att.id !== editingAttachment.id
          );
          onAttachmentsChange(updatedAttachments);

          showSuccessMessage("Attachment updated successfully");
        } catch (error) {
          showErrorMessage("Updated attachment uploaded, but failed to remove old version");
        }
        setEditingAttachment?.(null);
        setIsEditMode(false);
        processedAttachmentRef.current = null;
      }

      setShowCropModal(false);
      setShowDocumentModal(false);
      setCurrentFileIndex(null);
    }
  }, [
    currentFile,
    uploadFile,
    replaceFile,
    updateFile,
    compressImageFile,
    isEditMode,
    editingAttachment,
    fundingRequestId,
    onAttachmentsChange,
    setEditingAttachment
  ]);

  return (
    <>
      {currentFile && currentFile.type?.startsWith("image/") && (
        <CropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false);
            if (isEditMode) {
              setEditingAttachment?.(null);
              setIsEditMode(false);
              processedAttachmentRef.current = null;
            }
            if (currentFile) {
              removeFile(currentFile.id);
            }
            setCurrentFileIndex(null);
          }}
          file={{
            id: currentFile.id,
            preview: currentFile.preview ?? "",
            category: currentCategoryOptions,
            name: currentFile.name ?? ""
          }}
          categories={categories}
          onCategoryChange={(id: string, categories: IOption[]) => {
            const values = categories.map((option) => option.value);
            const value = values.length > 0 ? values.join(",") : "";
            updateFileCategory(id, value);
          }}
          onCrop={handleCrop}
          cropperRef={cropperRef}
          onVisibilityChange={updateFileVisibility}
          onUpload={handleUploadCurrentFile}
          isUploading={currentFile.status === "uploading"}
          uploadProgress={currentFile.progress || 0}
          isCompressing={isCompressing}
          compressionProgress={compressionProgress}
        />
      )}
      {currentFile && !currentFile.type?.startsWith("image/") && (
        <DocumentPreviewModal
          isOpen={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          file={{
            id: currentFile.id,
            name: currentFile.name ?? "",
            category: currentCategoryOptions,
            type: currentFile.type,
            preview: currentFile.preview
          }}
          categories={categories}
          onCategoryChange={(id: string, categories: IOption[]) => {
            const values = categories.map((option) => option.value);
            const value = values.length > 0 ? values.join(",") : "";
            updateFileCategory(id, value);
          }}
          onVisibilityChange={updateFileVisibility}
          onUpload={handleUploadCurrentFile}
          isUploading={currentFile.status === "uploading"}
          uploadProgress={currentFile.progress || 0}
          isCompressing={isCompressing}
          compressionProgress={compressionProgress}
        />
      )}
      <FileUploader
        isDragActive={isDragActive}
        isGlobalDisabled={false}
        loading={false}
        isUploading={isUploading}
        onDrag={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        inputRef={fileInputRef}
        onChange={handleFileInputChange}
      />
    </>
  );
};

const UploadSection: React.FC<UploadSectionProps> = (props) => (
  <FileUploadProvider>
    <UploadSectionInner {...props} />
  </FileUploadProvider>
);

export default UploadSection;
